// frontend/src/components/account/ProfilePanel.jsx

import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Card,
  Space,
  Typography,
  Divider,
  Tag,
  Row,
  Col,
  Modal,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../app.slice";
import { useUpdateProfileMutation } from "../account.service";
import { toast } from "react-toastify";

const { Title, Text } = Typography;

// ============================================
// ✅ ADDRESS FORM
// ============================================
const AddressForm = ({ address, onSave, onCancel, isLoading }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (address) {
      form.setFieldsValue(address);
    } else {
      form.resetFields();
    }
  }, [address, form]);

  const handleSubmit = async (values) => {
    onSave(values);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={address || {}}
    >
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item
            name="label"
            label="Address Label"
            rules={[{ required: true, message: "Please enter address label" }]}
          >
            <Input placeholder="e.g., Home, Office, Branch" prefix={<HomeOutlined />} />
          </Form.Item>
        </Col>
        <Col xs={24}>
          <Form.Item
            name="street"
            label="Street Address"
            rules={[{ required: true, message: "Please enter street address" }]}
          >
            <Input placeholder="123 Main Street" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: "Please enter city" }]}
          >
            <Input placeholder="Manchester" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="state" label="State/County">
            <Input placeholder="Greater Manchester" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="postalCode"
            label="Postal Code"
            rules={[{ required: true, message: "Please enter postal code" }]}
          >
            <Input placeholder="M1 1AE" />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: "Please enter country" }]}
            initialValue="GB"
          >
            <Input placeholder="United Kingdom" />
          </Form.Item>
        </Col>
      </Row>

      <Space style={{ width: "100%", justifyContent: "flex-end" }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="primary" htmlType="submit" loading={isLoading}>
          {address ? "Update Address" : "Add Address"}
        </Button>
      </Space>
    </Form>
  );
};

// ============================================
// ✅ ADDRESS CARD
// ============================================
const AddressCard = ({ address, onEdit, onDelete, isDefault, onSetDefault }) => {
  return (
    <div
      style={{
        border: isDefault ? "2px solid #6DB33F" : "1px solid #e8e8e8",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "12px",
        backgroundColor: isDefault ? "#f6ffed" : "#fff",
        transition: "all 0.2s",
        position: "relative",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
            <HomeOutlined style={{ color: "#6DB33F" }} />
            <Text strong>{address.label}</Text>
            {isDefault && <Tag color="green">Default</Tag>}
          </div>
          <Text style={{ display: "block", color: "#666", marginTop: "4px" }}>
            {address.street}
          </Text>
          <Text style={{ display: "block", color: "#666" }}>
            {address.city}, {address.state} {address.postalCode}
          </Text>
          <Text style={{ display: "block", color: "#666" }}>
            {address.country}
          </Text>
        </div>
        <Space>
          {!isDefault && (
            <Button
              type="link"
              size="small"
              onClick={onSetDefault}
              style={{ color: "#6DB33F" }}
            >
              Set as Default
            </Button>
          )}
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={onEdit}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onDelete}
          />
        </Space>
      </div>
    </div>
  );
};

// ============================================
// ✅ MAIN PROFILE PANEL
// ============================================
const ProfilePanel = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const [form] = Form.useForm();

  const [addresses, setAddresses] = useState([]);
  const [editingAddress, setEditingAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [updateProfile, { isLoading }] = useUpdateProfileMutation();

  // ✅ Load user data
  useEffect(() => {
    if (user) {
      form.setFieldsValue({
        name: user.name || "",
        email: user.email || "",
        phone: user.phone || "",
      });
      setAddresses(user.addresses || []);
    }
  }, [user, form]);

  // ============================================
  // ✅ GET DEFAULT ADDRESS
  // ============================================
  const getDefaultAddressId = () => {
    const defaultAddr = addresses.find((a) => a.isDefault);
    return defaultAddr?.id || null;
  };

  // ============================================
  // ✅ UPDATE PROFILE
  // ============================================
  const handleUpdateProfile = async (values) => {
    try {
      const result = await updateProfile({
        name: values.name,
        phone: values.phone,
        addresses: addresses,
        defaultAddressId: getDefaultAddressId(),
      }).unwrap();

      if (result.success) {
        toast.success("Profile updated successfully");
        dispatch(setUser(result.data.user));
      }
    } catch (error) {
      toast.error(error.data?.message || "Failed to update profile");
    }
  };

  // ============================================
  // ✅ ADDRESS CRUD
  // ============================================
  const handleAddAddress = (values) => {
    const newAddress = {
      id: `addr_${Date.now()}`,
      ...values,
      isDefault: addresses.length === 0, // First address becomes default
    };
    const newAddresses = [...addresses, newAddress];
    setAddresses(newAddresses);
    setShowAddressForm(false);
    toast.success("Address added successfully");
    
    // ✅ Auto-save to backend
    autoSaveAddresses(newAddresses);
  };

  const handleEditAddress = (address) => {
    setEditingAddress(address);
    setShowAddressForm(true);
  };

  const handleUpdateAddress = (values) => {
    const updatedAddresses = addresses.map((addr) =>
      addr.id === editingAddress.id ? { ...addr, ...values } : addr
    );
    setAddresses(updatedAddresses);
    setEditingAddress(null);
    setShowAddressForm(false);
    toast.success("Address updated successfully");
    
    // ✅ Auto-save to backend
    autoSaveAddresses(updatedAddresses);
  };

  const handleDeleteAddress = (addressId) => {
    Modal.confirm({
      title: "Delete Address",
      content: "Are you sure you want to delete this address?",
      okText: "Delete",
      okType: "danger",
      onOk: () => {
        const updatedAddresses = addresses.filter((addr) => addr.id !== addressId);
        setAddresses(updatedAddresses);
        toast.success("Address deleted successfully");
        
        // ✅ Auto-save to backend
        autoSaveAddresses(updatedAddresses);
      },
    });
  };

  const handleSetDefault = (addressId) => {
    const updatedAddresses = addresses.map((addr) => ({
      ...addr,
      isDefault: addr.id === addressId,
    }));
    setAddresses(updatedAddresses);
    toast.success("Default address updated");
    
    // ✅ Auto-save to backend
    autoSaveAddresses(updatedAddresses);
  };

  // ============================================
  // ✅ AUTO-SAVE ADDRESSES TO BACKEND
  // ============================================
  const autoSaveAddresses = async (newAddresses) => {
    try {
      const result = await updateProfile({
        name: form.getFieldValue("name"),
        phone: form.getFieldValue("phone"),
        addresses: newAddresses,
        defaultAddressId: newAddresses.find((a) => a.isDefault)?.id || null,
      }).unwrap();

      if (result.success) {
        dispatch(setUser(result.data.user));
      }
    } catch (error) {
      toast.error("Failed to save addresses");
    }
  };

  // ============================================
  // ✅ RENDER
  // ============================================
  return (
    <div style={{ padding: "16px 0" }}>
      {/* ✅ Basic Profile */}
      <Title level={4}>Profile Information</Title>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleUpdateProfile}
        style={{ maxWidth: "500px" }}
      >
        <Form.Item
          name="name"
          label="Full Name"
          rules={[{ required: true, message: "Please enter your name" }]}
        >
          <Input prefix={<UserOutlined />} placeholder="Your Name" size="large" />
        </Form.Item>

        <Form.Item name="email" label="Email Address">
          <Input prefix={<MailOutlined />} disabled placeholder="Email" size="large" />
        </Form.Item>

        <Form.Item name="phone" label="Phone Number">
          <Input prefix={<PhoneOutlined />} placeholder="+44 123 456 7890" size="large" />
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={isLoading} size="large">
            Update Profile
          </Button>
        </Form.Item>
      </Form>

      <Divider />

      {/* ✅ Addresses Section */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Title level={4}>Saved Addresses</Title>
        {!showAddressForm && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingAddress(null);
              setShowAddressForm(true);
            }}
          >
            Add Address
          </Button>
        )}
      </div>

      <Text type="secondary" style={{ display: "block", marginBottom: "16px" }}>
        Manage your shipping addresses. Click "Set as Default" to select your primary address.
      </Text>

      {/* ✅ Address Form */}
      {showAddressForm && (
        <Card style={{ marginBottom: "16px", background: "#fafafa" }}>
          <AddressForm
            address={editingAddress}
            onSave={editingAddress ? handleUpdateAddress : handleAddAddress}
            onCancel={() => {
              setShowAddressForm(false);
              setEditingAddress(null);
            }}
            isLoading={isLoading}
          />
        </Card>
      )}

      {/* ✅ Address List */}
      {addresses.length === 0 && !showAddressForm && (
        <div style={{ textAlign: "center", padding: "40px", color: "#999" }}>
          <HomeOutlined style={{ fontSize: "48px" }} />
          <p style={{ marginTop: "16px" }}>No addresses added yet.</p>
          <Button type="link" onClick={() => setShowAddressForm(true)}>
            Add your first address
          </Button>
        </div>
      )}

      {addresses.map((address) => (
        <AddressCard
          key={address.id}
          address={address}
          isDefault={address.isDefault || false}
          onEdit={() => handleEditAddress(address)}
          onDelete={() => handleDeleteAddress(address.id)}
          onSetDefault={() => handleSetDefault(address.id)}
        />
      ))}

      <Divider />

      {/* ✅ Summary */}
      <div
        style={{
          background: "#f6ffed",
          padding: "16px",
          borderRadius: "8px",
          border: "1px solid #b7eb8f",
        }}
      >
        <Text strong>Default Shipping Address:</Text>
        {addresses.find((a) => a.isDefault) ? (
          <div>
            {addresses
              .filter((a) => a.isDefault)
              .map((a) => (
                <Text key={a.id} style={{ display: "block", color: "#666" }}>
                  {a.label}: {a.street}, {a.city}, {a.postalCode}
                </Text>
              ))}
          </div>
        ) : (
          <Text type="secondary">No default address selected</Text>
        )}
      </div>
    </div>
  );
};

export default ProfilePanel;